// Direct browser → Cloudflare R2 multipart uploader.
//
// Flow:
//   1. POST /api/uploads/init-multipart  → {r2_key, upload_id, part_size, part_urls}
//   2. For each part: PUT blob to presigned URL, capture ETag from response
//   3. POST /api/uploads/complete-multipart → server finalises the object
//
// If ANY part exhausts its retries, we call /api/uploads/abort-multipart so
// R2 doesn't accumulate orphan parts (they cost money).

import axios from "axios";
import { api } from "./api";

// Part size — S3/R2 requires ≥ 5 MiB per part (except the last). 50 MiB keeps
// the presigned-URL list short even for 5 GB files (max 100 parts).
const PART_SIZE = 50 * 1024 * 1024;
const MAX_ATTEMPTS_PER_PART = 3;

export async function uploadFileToR2(file, onProgress) {
  if (!file) throw new Error("no file");
  const partCount = Math.max(1, Math.ceil(file.size / PART_SIZE));

  // 1) init
  const initRes = await api.post("/uploads/init-multipart", {
    filename: file.name,
    content_type: file.type || "application/octet-stream",
    size: file.size,
    part_count: partCount,
  });
  const { r2_key, upload_id, part_urls, part_size } = initRes.data;

  // 2) upload parts
  const parts = [];
  let uploadedBytes = 0;
  const partBytes = new Array(partCount).fill(0);

  try {
    for (let i = 0; i < partCount; i++) {
      const start = i * part_size;
      const end = Math.min(start + part_size, file.size);
      const blob = file.slice(start, end);
      const { url, part_number } = part_urls[i];

      let etag = null;
      let lastErr = null;
      for (let attempt = 0; attempt < MAX_ATTEMPTS_PER_PART; attempt++) {
        try {
          const res = await axios.put(url, blob, {
            // Do NOT set Content-Type — the presigned URL was signed without
            // one, and any header we add here has to match the signature.
            headers: {},
            timeout: 0,
            maxContentLength: Infinity,
            maxBodyLength: Infinity,
            withCredentials: false,
            onUploadProgress: (evt) => {
              if (!onProgress || !evt) return;
              partBytes[i] = evt.loaded || 0;
              const cumulative = uploadedBytes + partBytes[i];
              const pct = Math.min(99, Math.round((cumulative / file.size) * 100));
              onProgress(pct);
            },
          });
          // R2 returns the ETag in the response headers; axios lowercases them.
          etag = res.headers.etag || res.headers.ETag;
          if (!etag) throw new Error("R2 did not return an ETag for part " + part_number);
          break;
        } catch (err) {
          lastErr = err;
          partBytes[i] = 0;
        }
      }
      if (!etag) throw lastErr || new Error("part " + part_number + " failed");
      parts.push({ PartNumber: part_number, ETag: etag });
      uploadedBytes += end - start;
      partBytes[i] = end - start;
    }
  } catch (err) {
    // Best-effort abort so we don't leak paid-for parts on R2.
    try {
      await api.post("/uploads/abort-multipart", { r2_key, upload_id });
    } catch { /* ignore — sweeper will catch it */ }
    throw err;
  }

  // 3) complete
  const completeRes = await api.post("/uploads/complete-multipart", {
    r2_key,
    upload_id,
    parts,
  });
  const finalKey = completeRes.data?.r2_key;
  if (!finalKey || typeof finalKey !== "string") {
    // Defensive — if the backend contract ever changes we want a loud,
    // debuggable error here rather than shipping the string "undefined"
    // to /subtitles/generate and getting a confusing 403 later.
    throw new Error("R2 complete response missing r2_key");
  }
  onProgress?.(100);
  return { r2_key: finalKey, size: completeRes.data.size };
}
